import React, { useEffect, useMemo, useState } from 'react';
import { EmptyState, ErrorIndicator, LoadingIndicator } from './common/LoadingStates';
import { PendingDeltaApi, PendingDeltaEntry, PendingDeltaResponse } from '../services/Ai/PendingDeltaService';
import './PendingDeltaPanel.scss';

interface PendingDeltaPanelProps {
    stage: string;
    realm: string;
    trackerData: {
        timeWindow?: string;
        clientId?: string;
        metadata?: {
            generatedAt?: string;
            [key: string]: any;
        };
    } | null;
}

type SortMode = 'impact' | 'increase' | 'decrease' | 'alphabetical';

const SORT_OPTIONS: Array<{ value: SortMode; label: string }> = [
    { value: 'impact', label: 'Biggest movement' },
    { value: 'increase', label: 'Largest increase' },
    { value: 'decrease', label: 'Largest recovery' },
    { value: 'alphabetical', label: 'Alphabetical' },
];

const parseNumber = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsedValue = Number(value);
        return Number.isFinite(parsedValue) ? parsedValue : 0;
    }

    return 0;
};

const unwrapPendingDeltaPayload = (value: unknown): unknown => {
    if (typeof value === 'string') {
        try {
            return unwrapPendingDeltaPayload(JSON.parse(value));
        } catch {
            return value;
        }
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    const candidate = value as Record<string, unknown>;
    if (Array.isArray(candidate.processors) || Array.isArray(candidate.clients)) {
        return candidate;
    }

    if ('body' in candidate) {
        return unwrapPendingDeltaPayload(candidate.body);
    }

    if ('data' in candidate && Object.keys(candidate).length === 1) {
        return unwrapPendingDeltaPayload(candidate.data);
    }

    return candidate;
};

const normalizeEntry = (value: unknown): PendingDeltaEntry | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    const label = typeof candidate.label === 'string' ? candidate.label : null;
    if (!label) {
        return null;
    }

    const normalizeIds = (ids: unknown): string[] =>
        Array.isArray(ids)
            ? ids.filter((id): id is string => typeof id === 'string' && id.trim() !== '')
            : [];

    return {
        label,
        startCount: parseNumber(candidate.startCount),
        endCount: parseNumber(candidate.endCount),
        absoluteChange: parseNumber(candidate.absoluteChange),
        netNewCount: parseNumber(candidate.netNewCount),
        netRecoveredCount: parseNumber(candidate.netRecoveredCount),
        netNewSampleRefundIds: normalizeIds(candidate.netNewSampleRefundIds),
        netRecoveredSampleRefundIds: normalizeIds(candidate.netRecoveredSampleRefundIds),
    };
};

const normalizePendingDeltaResponse = (value: unknown): PendingDeltaResponse | null => {
    const payload = unwrapPendingDeltaPayload(value);
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const candidate = payload as Record<string, unknown>;
    const processors = Array.isArray(candidate.processors)
        ? candidate.processors.map(normalizeEntry).filter((entry): entry is PendingDeltaEntry => Boolean(entry))
        : null;
    const clients = Array.isArray(candidate.clients)
        ? candidate.clients.map(normalizeEntry).filter((entry): entry is PendingDeltaEntry => Boolean(entry))
        : null;

    if (!processors || !clients) {
        return null;
    }

    return {
        generatedAt: typeof candidate.generatedAt === 'string' ? candidate.generatedAt : new Date().toISOString(),
        timeWindow: typeof candidate.timeWindow === 'string' ? candidate.timeWindow : 'now-30d',
        clientId: typeof candidate.clientId === 'string' ? candidate.clientId : 'AllClients',
        requestedWindowStart: typeof candidate.requestedWindowStart === 'string' ? candidate.requestedWindowStart : null,
        requestedWindowEnd: typeof candidate.requestedWindowEnd === 'string' ? candidate.requestedWindowEnd : null,
        processorWindowStart: typeof candidate.processorWindowStart === 'string' ? candidate.processorWindowStart : null,
        processorWindowEnd: typeof candidate.processorWindowEnd === 'string' ? candidate.processorWindowEnd : null,
        clientWindowStart: typeof candidate.clientWindowStart === 'string' ? candidate.clientWindowStart : null,
        clientWindowEnd: typeof candidate.clientWindowEnd === 'string' ? candidate.clientWindowEnd : null,
        processorSnapshotsAnalyzed: parseNumber(candidate.processorSnapshotsAnalyzed),
        clientSnapshotsAnalyzed: parseNumber(candidate.clientSnapshotsAnalyzed),
        processors,
        clients,
    };
};

const formatCount = (value: number): string => value.toLocaleString('en-US');

const formatDelta = (value: number): string => {
    if (value === 0) {
        return '0';
    }
    return `${value > 0 ? '+' : ''}${formatCount(value)}`;
};

const formatWindow = (start?: string | null, end?: string | null): string => {
    if (!start || !end) {
        return 'No retained boundary snapshots yet';
    }

    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return `${start} to ${end}`;
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
    });

    return `${formatter.format(startDate)} to ${formatter.format(endDate)} UTC`;
};

const sortEntries = (entries: PendingDeltaEntry[], mode: SortMode): PendingDeltaEntry[] => {
    const sortedEntries = [...entries];
    sortedEntries.sort((left, right) => {
        switch (mode) {
            case 'increase':
                return right.absoluteChange - left.absoluteChange
                    || right.endCount - left.endCount
                    || left.label.localeCompare(right.label);
            case 'decrease':
                return left.absoluteChange - right.absoluteChange
                    || left.startCount - right.startCount
                    || left.label.localeCompare(right.label);
            case 'alphabetical':
                return left.label.localeCompare(right.label);
            case 'impact':
            default:
                return Math.abs(right.absoluteChange) - Math.abs(left.absoluteChange)
                    || right.endCount - left.endCount
                    || left.label.localeCompare(right.label);
        }
    });
    return sortedEntries;
};

const PendingDeltaPanel: React.FC<PendingDeltaPanelProps> = ({ stage, realm, trackerData }) => {
    const [response, setResponse] = useState<PendingDeltaResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortMode, setSortMode] = useState<SortMode>('impact');
    const [selectedProcessorLabel, setSelectedProcessorLabel] = useState<string | null>(null);
    const [selectedClientLabel, setSelectedClientLabel] = useState<string | null>(null);

    useEffect(() => {
        if (!trackerData?.timeWindow || !trackerData?.clientId) {
            setResponse(null);
            setError(null);
            return;
        }

        let active = true;

        const loadPendingDeltas = async () => {
            setLoading(true);
            setError(null);
            try {
                const apiResponse = await PendingDeltaApi.getPendingDeltas(
                    stage,
                    realm,
                    trackerData.timeWindow || 'now-30d',
                    trackerData.clientId || 'AllClients',
                );
                const normalizedResponse = normalizePendingDeltaResponse(apiResponse);
                if (!normalizedResponse) {
                    throw new Error('Pending delta API returned an unexpected payload shape');
                }

                if (active) {
                    setResponse(normalizedResponse);
                }
            } catch (err) {
                if (!active) {
                    return;
                }
                setResponse(null);
                setError(err instanceof Error ? err.message : 'Failed to load pending delta history');
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadPendingDeltas();

        return () => {
            active = false;
        };
    }, [stage, realm, trackerData?.timeWindow, trackerData?.clientId, trackerData?.metadata?.generatedAt]);

    const sortedProcessors = useMemo(
        () => sortEntries(response?.processors ?? [], sortMode),
        [response?.processors, sortMode],
    );
    const sortedClients = useMemo(
        () => sortEntries(response?.clients ?? [], sortMode),
        [response?.clients, sortMode],
    );

    useEffect(() => {
        if (!sortedProcessors.length) {
            setSelectedProcessorLabel(null);
            return;
        }

        if (!selectedProcessorLabel || !sortedProcessors.some((entry) => entry.label === selectedProcessorLabel)) {
            setSelectedProcessorLabel(sortedProcessors[0].label);
        }
    }, [selectedProcessorLabel, sortedProcessors]);

    useEffect(() => {
        if (!sortedClients.length) {
            setSelectedClientLabel(null);
            return;
        }

        if (!selectedClientLabel || !sortedClients.some((entry) => entry.label === selectedClientLabel)) {
            setSelectedClientLabel(sortedClients[0].label);
        }
    }, [selectedClientLabel, sortedClients]);

    if (!trackerData) {
        return null;
    }

    return (
        <section className="pending-delta-panel">
            <div className="pending-delta-panel__header">
                <div>
                    <p className="eyebrow">Pending Delta</p>
                    <h2>Pending movement</h2>
                </div>
                <div className="pending-delta-panel__controls">
                    <label htmlFor="pending-delta-sort">Sort</label>
                    <select
                        id="pending-delta-sort"
                        value={sortMode}
                        onChange={(event) => setSortMode(event.target.value as SortMode)}
                    >
                        {SORT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading && <LoadingIndicator message="Loading pending deltas..." />}
            {!loading && error && <ErrorIndicator error={error} />}

            {!loading && !error && response && response.processors.length === 0 && response.clients.length === 0 && (
                <EmptyState
                    title="Pending delta history is still building"
                    message="Sherlock needs retained over-SLA refund and PMR snapshots in this scope before it can graph processor and client movement."
                />
            )}

            {!loading && !error && response && (response.processors.length > 0 || response.clients.length > 0) && (
                <div className="pending-delta-panel__graph-grid">
                    <PendingDeltaGraph
                        title="Processor movement"
                        subtitle="Processors in scope."
                        entries={sortedProcessors}
                        selectedLabel={selectedProcessorLabel}
                        onSelect={setSelectedProcessorLabel}
                        snapshotsAnalyzed={response.processorSnapshotsAnalyzed}
                        windowText={formatWindow(response.processorWindowStart, response.processorWindowEnd)}
                    />
                    <PendingDeltaGraph
                        title="Client movement"
                        subtitle="Clients in scope."
                        entries={sortedClients}
                        selectedLabel={selectedClientLabel}
                        onSelect={setSelectedClientLabel}
                        snapshotsAnalyzed={response.clientSnapshotsAnalyzed}
                        windowText={formatWindow(response.clientWindowStart, response.clientWindowEnd)}
                    />
                </div>
            )}
        </section>
    );
};

const PendingDeltaGraph: React.FC<{
    title: string;
    subtitle: string;
    entries: PendingDeltaEntry[];
    selectedLabel: string | null;
    onSelect: (label: string) => void;
    snapshotsAnalyzed: number;
    windowText: string;
}> = ({ title, subtitle, entries, selectedLabel, onSelect, snapshotsAnalyzed, windowText }) => {
    const maxAbsDelta = useMemo(
        () => entries.reduce((currentMax, entry) => Math.max(currentMax, Math.abs(entry.absoluteChange)), 0),
        [entries],
    );
    const selectedEntry = entries.find((entry) => entry.label === selectedLabel) ?? entries[0] ?? null;

    return (
        <div className="pending-delta-graph">
            <div className="pending-delta-graph__header">
                <div>
                    <h3>{title}</h3>
                    <p>{subtitle}</p>
                </div>
                <div className="pending-delta-graph__meta">
                    <span>{snapshotsAnalyzed} snapshots</span>
                    <span>{windowText}</span>
                </div>
            </div>

            {entries.length === 0 ? (
                <div className="pending-delta-graph__empty">No retained movement in this scope yet.</div>
            ) : (
                <>
                    <div className="pending-delta-graph__viewport">
                        {entries.map((entry) => (
                            <PendingDeltaRow
                                key={entry.label}
                                entry={entry}
                                maxAbsDelta={maxAbsDelta}
                                selected={entry.label === selectedLabel}
                                onSelect={() => onSelect(entry.label)}
                            />
                        ))}
                    </div>

                    {selectedEntry && (
                        <div className="pending-delta-detail">
                            <div className="pending-delta-detail__header">
                                <h4>{selectedEntry.label}</h4>
                                <span className={selectedEntry.absoluteChange > 0 ? 'up' : selectedEntry.absoluteChange < 0 ? 'down' : 'flat'}>
                                    {formatDelta(selectedEntry.absoluteChange)}
                                </span>
                            </div>

                            <div className="pending-delta-detail__stats">
                                <div>
                                    <span>Window start</span>
                                    <strong>{formatCount(selectedEntry.startCount)}</strong>
                                </div>
                                <div>
                                    <span>Latest</span>
                                    <strong>{formatCount(selectedEntry.endCount)}</strong>
                                </div>
                                <div>
                                    <span>Net new</span>
                                    <strong>{formatCount(selectedEntry.netNewCount)}</strong>
                                </div>
                                <div>
                                    <span>Net recovered</span>
                                    <strong>{formatCount(selectedEntry.netRecoveredCount)}</strong>
                                </div>
                            </div>

                            <SampleIdGroup
                                title="Net-new sample refund IDs"
                                ids={selectedEntry.netNewSampleRefundIds}
                                emptyText="No net-new refund IDs in the retained boundary comparison."
                            />
                            <SampleIdGroup
                                title="Recovered sample refund IDs"
                                ids={selectedEntry.netRecoveredSampleRefundIds}
                                emptyText="No recovered refund IDs in the retained boundary comparison."
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const PendingDeltaRow: React.FC<{
    entry: PendingDeltaEntry;
    maxAbsDelta: number;
    selected: boolean;
    onSelect: () => void;
}> = ({ entry, maxAbsDelta, selected, onSelect }) => {
    const magnitude = Math.abs(entry.absoluteChange);
    const widthPercent = maxAbsDelta === 0 || magnitude === 0
        ? 0
        : Math.max((magnitude / maxAbsDelta) * 100, 8);

    return (
        <button
            type="button"
            className={`pending-delta-row ${selected ? 'selected' : ''}`}
            onClick={onSelect}
            aria-pressed={selected}
        >
            <span className="pending-delta-row__label">{entry.label}</span>
            <span className="pending-delta-row__track" aria-hidden="true">
                <span className="pending-delta-row__track-side pending-delta-row__track-side--left">
                    {entry.absoluteChange < 0 && (
                        <span
                            className="pending-delta-row__bar pending-delta-row__bar--recovered"
                            style={{ width: `${widthPercent}%` }}
                        />
                    )}
                </span>
                <span className="pending-delta-row__baseline" />
                <span className="pending-delta-row__track-side pending-delta-row__track-side--right">
                    {entry.absoluteChange > 0 && (
                        <span
                            className="pending-delta-row__bar pending-delta-row__bar--new"
                            style={{ width: `${widthPercent}%` }}
                        />
                    )}
                </span>
            </span>
            <span className="pending-delta-row__counts">
                <span>{formatCount(entry.startCount)} to {formatCount(entry.endCount)}</span>
                <strong className={entry.absoluteChange > 0 ? 'up' : entry.absoluteChange < 0 ? 'down' : 'flat'}>
                    {formatDelta(entry.absoluteChange)}
                </strong>
            </span>
        </button>
    );
};

const SampleIdGroup: React.FC<{
    title: string;
    ids: string[];
    emptyText: string;
}> = ({ title, ids, emptyText }) => (
    <div className="pending-delta-detail__samples">
        <h5>{title}</h5>
        {ids.length === 0 ? (
            <p>{emptyText}</p>
        ) : (
            <div className="pending-delta-detail__sample-list">
                {ids.map((id) => (
                    <span key={id} className="pending-delta-detail__sample-chip">{id}</span>
                ))}
            </div>
        )}
    </div>
);

export default PendingDeltaPanel;
